import { Link, useParams } from "wouter";
import { useGetSpace, useListEvents, getGetSpaceQueryKey } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import cozyInterior from "@assets/IMG_6898_1779819286654.jpeg";
import { format } from "date-fns";
import { motion } from "framer-motion";

const NEW_CITY_TOUR_URL = "https://playcanv.as/b/d08f7071";
const BLOOD_ECLIPSE_TOUR_URL = "https://playcanv.as/b/dde2afd4";

export default function SpaceDetail() {
  const params = useParams();
  const id = Number(params.id);

  const { data: space, isLoading: spaceLoading } = useGetSpace(id, {
    query: { enabled: !!id, queryKey: getGetSpaceQueryKey(id) }
  });

  const { data: events, isLoading: eventsLoading } = useListEvents({
    spaceId: id,
  });

  if (spaceLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col pt-20 pb-24">
        <div className="h-[50vh] bg-card/50 animate-pulse w-full" />
        <div className="container mx-auto px-4 mt-8 space-y-6">
          <div className="h-12 w-1/3 bg-card/50 animate-pulse rounded" />
          <div className="h-24 w-2/3 bg-card/50 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Space Not Found</h1>
          <p className="text-muted-foreground">The sector you are looking for does not exist or has been archived.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Hero Header */}
      <div className="relative h-[60vh] w-full">
        <img src={space.imageUrl || cozyInterior} alt={space.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        
        <div className="absolute bottom-0 left-0 w-full p-8 container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-end"
          >
            <div>
              <div className="flex gap-3 mb-3">
                <span className="bg-primary/20 text-primary border border-primary/30 px-3 py-1 rounded text-xs font-mono uppercase tracking-wider">
                  {space.regionLabel || space.region}
                </span>
                <span className="bg-white/10 text-white border border-white/20 px-3 py-1 rounded text-xs font-mono uppercase tracking-wider">
                  {space.category}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tighter uppercase text-white drop-shadow-lg">{space.name}</h1>
            </div>
            
            <div className="hidden md:flex bg-black/60 backdrop-blur-md border border-white/10 p-4 rounded-xl items-center gap-6">
              <div className="text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Status</p>
                <div className="flex items-center gap-2 font-mono text-sm text-green-400">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  ONLINE
                </div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Citizens</p>
                <p className="font-mono text-sm">{space.onlineCount} / {space.maxCapacity}</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Time</p>
                <p className="font-mono text-sm uppercase">{space.timeOfDay}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-12">
          <section>
            <h2 className="text-2xl font-bold uppercase tracking-tight mb-4 border-b border-white/10 pb-4">About this Space</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">{space.description}</p>
          </section>

          {id === 13 && (
            <section>
              <div className="flex items-end justify-between gap-4 mb-6 border-b border-white/10 pb-4">
                <div>
                  <h2 className="text-2xl font-bold uppercase tracking-tight">ISM | New City</h2>
                  <p className="text-sm text-muted-foreground mt-1">Take a guided tour of the linked 'ISM | New City' 3D tour space.</p>
                </div>
                <a
                  href={NEW_CITY_TOUR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex text-xs font-mono uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
                >
                  Open in new tab ↗
                </a>
              </div>
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black shadow-2xl">
                <iframe
                  src={NEW_CITY_TOUR_URL}
                  title="ISM | New City PlayCanvas tour"
                  className="h-full w-full border-0"
                  allow="autoplay; fullscreen; gamepad; xr-spatial-tracking"
                  loading="lazy"
                />
              </div>
              <a
                href={NEW_CITY_TOUR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex text-xs font-mono uppercase tracking-widest text-primary hover:text-primary/80 transition-colors sm:hidden"
              >
                Open tour in new tab ↗
              </a>
            </section>
          )}

          {id === 20 && (
            <section>
              <div className="flex items-end justify-between gap-4 mb-6 border-b border-white/10 pb-4">
                <div>
                  <h2 className="text-2xl font-bold uppercase tracking-tight">Blood Eclipse Green House</h2>
                  <p className="text-sm text-muted-foreground mt-1">Enter the living sanctuary of the Blood Eclipse Queen.</p>
                </div>
                <a
                  href={BLOOD_ECLIPSE_TOUR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:inline-flex text-xs font-mono uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
                >
                  Open in new tab ↗
                </a>
              </div>
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-black shadow-2xl">
                <iframe
                  src={BLOOD_ECLIPSE_TOUR_URL}
                  title="Blood Eclipse Green House PlayCanvas tour"
                  className="h-full w-full border-0"
                  allow="autoplay; fullscreen; gamepad; xr-spatial-tracking"
                  loading="lazy"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                <Link
                  href="/marketplace/35"
                  className="rounded-lg border border-primary/30 bg-primary/5 p-4 hover:bg-primary/10 transition-colors"
                >
                  <span className="block text-xs font-mono uppercase tracking-widest text-primary mb-2">Featured Artwork</span>
                  <span className="block font-bold text-white">Queen's of the Jungle ↗</span>
                  <span className="block text-xs text-muted-foreground mt-1">View the Blood Eclipse artwork.</span>
                </Link>
                <Link
                  href="/creators/illusionistz/post/101"
                  className="rounded-lg border border-primary/30 bg-primary/5 p-4 hover:bg-primary/10 transition-colors"
                >
                  <span className="block text-xs font-mono uppercase tracking-widest text-primary mb-2">Blood Eclipse Series</span>
                  <span className="block font-bold text-white">Featured Content ↗</span>
                  <span className="block text-xs text-muted-foreground mt-1">Explore the featured series chapter.</span>
                </Link>
                <Link
                  href="/creators"
                  className="rounded-lg border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors"
                >
                  <span className="block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">ISM Creators</span>
                  <span className="block font-bold text-white">Explore the Creator Hub ↗</span>
                  <span className="block text-xs text-muted-foreground mt-1">Discover the wider ISM roster.</span>
                </Link>
                <Link
                  href="/creators/mst"
                  className="rounded-lg border border-violet-500/30 bg-violet-500/5 p-4 hover:bg-violet-500/10 transition-colors"
                >
                  <span className="block text-xs font-mono uppercase tracking-widest text-violet-300 mb-2">Featured Character</span>
                  <span className="block font-bold text-white">Ms. T · Blood Moon Queen ↗</span>
                  <span className="block text-xs text-muted-foreground mt-1">Visit Ms. T’s creator profile.</span>
                </Link>
              </div>
            </section>
          )}

          <section>
            <h2 className="text-2xl font-bold uppercase tracking-tight mb-6 border-b border-white/10 pb-4">Events in {space.name}</h2>
            {eventsLoading ? (
              <div className="h-48 bg-card/50 animate-pulse rounded-xl" />
            ) : events && events.length > 0 ? (
              <div className="space-y-4">
                {events.map(event => (
                  <Card key={event.id} className="bg-black/40 border-white/10 hover:border-primary/50 transition-colors backdrop-blur-sm">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-24 h-24 rounded bg-white/5 overflow-hidden flex-shrink-0">
                        <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg uppercase">{event.title}</h3>
                        <p className="text-sm text-primary font-mono mb-2">{format(new Date(event.startTime), "MMM do, HH:mm")}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{event.description}</p>
                      </div>
                      <Button variant="outline" className="hidden md:flex border-white/20 hover:bg-white/10 uppercase text-xs tracking-widest" data-testid={`btn-view-event-${event.id}`}>
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="bg-card/20 border border-white/10 p-8 rounded-xl text-center">
                <p className="text-muted-foreground">No events currently scheduled in this space.</p>
              </div>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-card/40 border border-white/10 p-6 rounded-xl backdrop-blur-sm sticky top-24">
            <h3 className="text-xl font-bold uppercase tracking-tight mb-6">Access Protocol</h3>
            {id === 13 ? (
              <a
                href={NEW_CITY_TOUR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-14 inline-flex items-center justify-center rounded-md bg-primary hover:bg-primary/80 text-white font-bold tracking-widest uppercase mb-4 transition-colors"
                data-testid="btn-take-tour"
              >
                TAKE TOUR ↗
              </a>
            ) : id === 20 ? (
              <a
                href={BLOOD_ECLIPSE_TOUR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-14 inline-flex items-center justify-center rounded-md bg-primary hover:bg-primary/80 text-white font-bold tracking-widest uppercase mb-4 transition-colors"
                data-testid="btn-blood-eclipse-tour"
              >
                ENTER GREEN HOUSE ↗
              </a>
            ) : (
              <Button size="lg" className="w-full h-14 bg-primary hover:bg-primary/80 text-white font-bold tracking-widest uppercase mb-4" data-testid="btn-initiate-transfer">
                INITIATE TRANSFER
              </Button>
            )}
            <p className="text-xs text-center text-muted-foreground font-mono">
              {id === 13 ? "Explore ISM | New City in the linked PlayCanvas space." : id === 20 ? "Enter the linked Blood Eclipse Green House space." : "Requires ISM compatible wallet. Transfer takes ~2s."}
            </p>

            {/* Viverse Worlds — shown for spaces with linked Viverse destinations */}
            {id === 13 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Viverse Worlds</h4>
                <a
                  href="https://www.viverse.com/yMAtq95"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full h-12 rounded-md border border-purple-500/50 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 hover:border-purple-400/70 font-bold text-xs tracking-widest uppercase transition-colors"
                >
                  AK Roof Top Vibez ↗
                </a>
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-white/10">
              <h4 className="text-sm font-bold uppercase text-muted-foreground mb-4">Space Attributes</h4>
              <ul className="space-y-3 font-mono text-sm">
                <li className="flex justify-between">
                  <span className="text-white/50">Coordinates</span>
                  <span>{space.coordinates || "Unknown"}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-white/50">City</span>
                  <span>{space.cityName || "N/A"}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-white/50">Environment</span>
                  <span className="uppercase">{space.category}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
